import { computed, MaybeRef, unref } from 'vue'
import { RateType } from '@/gql/graphql'
import { $t } from '@/composables/translation'
import {
  aboveMaximumMessage,
  RateCardBand,
  RateCardGuidance,
  rateCardTooltipText,
  useNoMatchGuidance,
  useRateCardGuidanceBandFields,
} from '@/components/Job/composables/rateCardGuidance'

export type { RateCardCriterion, RateCardBand, RateCardGuidance } from '@/components/Job/composables/rateCardGuidance'

export function useRateCardBandGuidance(options: {
  band: MaybeRef<RateCardBand>
  hasMsp: MaybeRef<boolean>
  currencySymbol: MaybeRef<string>
  // Band still reflects the last-saved rate type — stale once unsaved.
  rateTypeIsUnsaved: MaybeRef<boolean>
  currentRateType: MaybeRef<RateType>
}) {
  const noMatchGuidance = useNoMatchGuidance()

  const band = computed(() => unref(options.band))
  const hasMsp = computed(() => unref(options.hasMsp))
  const rateTypeIsUnsaved = computed(() => unref(options.rateTypeIsUnsaved))
  const currentRateType = computed(() => unref(options.currentRateType))

  const hasMatch = computed(() => band.value != null && band.value.noMatchReason == null)

  const bandRateTypeStale = computed(
    () =>
      hasMatch.value &&
      band.value?.rateType != null &&
      band.value.rateType !== currentRateType.value,
  )

  const rateTypeNotCurrent = computed(() => rateTypeIsUnsaved.value || bandRateTypeStale.value)

  // Whether the band may drive a warning/prefill, vs. only describing itself.
  const canActOnBand = computed(() => hasMatch.value && !hasMsp.value && !rateTypeNotCurrent.value)

  const { rangeAmountText, unitText, validUntilText } = useRateCardGuidanceBandFields(
    band,
    options.currencySymbol,
  )

  const guidance = computed<RateCardGuidance | undefined>(() => {
    if (!band.value || rateTypeNotCurrent.value) return undefined

    if (!hasMatch.value) return noMatchGuidance(band.value.noMatchReason)

    return {
      hasMatch: true,
      introText: $t('Your rate card suggests'),
      rangeAmountText: rangeAmountText.value,
      unitText: unitText.value,
      excludesMspFee: hasMsp.value,
      validUntilText: validUntilText.value,
      criteria: band.value.criteria ?? [],
      tooltipText: rateCardTooltipText(false),
    }
  })

  const fieldWarning = (entered: string | number | undefined | null): string | undefined => {
    if (!canActOnBand.value) return undefined

    return aboveMaximumMessage(entered, band.value?.maximum, unref(options.currencySymbol))
  }

  return { canActOnBand, guidance, fieldWarning }
}
